import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../dashboard/DashboardLayout';
import SettingsWizard from '../../components/SettingsWizard';
import { getFormControlConfigs, checkExtensionStatus } from '../../utils/extensionSync';
import { checkWizardCompletion, getWizardCompletionPercentage } from '../../utils/wizardUtils';
import { 
  Settings, 
  FileText, 
  Sliders, 
  ExternalLink, 
  Download, 
  Upload, 
  Filter, 
  Sparkles, 
  ShieldCheck,
  Cpu,
  Database,
  ArrowRight,
  RefreshCw
} from 'lucide-react';
import { trackPageView, trackButtonClick } from '../../utils/analytics';

const SettingsPage = () => {
  const navigate = useNavigate();
  const [extensionStatus, setExtensionStatus] = useState({ available: false, synced: false });
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const [wizardStatus, setWizardStatus] = useState({ completed: false, percentage: 0 });
  const [loadingStatus, setLoadingStatus] = useState(true);

  useEffect(() => {
    trackPageView("/settings", "Settings Overview");
    checkStatus();
    checkWizardStatus();
    checkFirstTimeSetup();
  }, []);

  const checkStatus = async () => {
    const status = await checkExtensionStatus();
    setExtensionStatus(status);
  };

  const checkWizardStatus = async () => {
    setLoadingStatus(true);
    try {
      const completion = await checkWizardCompletion();
      const percentage = await getWizardCompletionPercentage();
      setWizardStatus({
        completed: completion.isComplete,
        percentage: percentage.percentage,
        ...completion
      });
    } catch (e) {
      console.error('Failed to check wizard status:', e);
    } finally {
      setLoadingStatus(false);
    }
  };

  const checkFirstTimeSetup = async () => {
    try {
      const configs = await getFormControlConfigs();
      const hasConfig = configs.defaultFields?.firstName || configs.inputFieldConfigs?.length > 0;
      const wizardCompleted = localStorage.getItem('applyease_wizard_completed');
      if (!hasConfig && !wizardCompleted) {
        setShowWizard(true);
      }
    } catch (e) {
      console.error('Failed to check setup:', e);
    }
  };

  const handleWizardComplete = () => {
    localStorage.setItem('applyease_wizard_completed', 'true');
    setShowWizard(false);
    checkWizardStatus();
  };

  const handleStartWizard = () => {
    trackButtonClick("Setup Wizard", "Settings");
    setShowWizard(true);
  };

  const handleExport = async () => {
    trackButtonClick("Export Settings", "Settings");
    setExporting(true);
    try {
      const configs = await getFormControlConfigs();
      const dataStr = JSON.stringify(configs, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `applyease-settings-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      alert('Failed to export settings: ' + error.message);
    } finally {
      setExporting(false);
    }
  };

  const handleImport = () => {
    trackButtonClick("Import Settings", "Settings");
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;

      setImporting(true);
      try {
        const text = await file.text();
        const configs = JSON.parse(text);
        if (!configs.defaultFields || !configs.inputFieldConfigs) throw new Error('Invalid format');
        alert('Settings imported successfully! Please review in Form Control.');
        navigate('/form-control', { state: { importConfigs: configs } });
      } catch (error) {
        alert('Import failed: ' + error.message);
      } finally {
        setImporting(false);
      }
    };
    input.click();
  };

  if (showWizard) {
    return (
      <DashboardLayout activeTab="settings">
        <SettingsWizard onComplete={handleWizardComplete} />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout activeTab="settings" setTab={(t) => navigate('/dashboard', { state: { tab: t } })}>
      <div className="max-w-7xl mx-auto space-y-10 animate-in fade-in slide-in-from-bottom-4 duration-500 pb-20">
        
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
           <div className="flex flex-col text-center md:text-left">
              <h2 className="text-3xl font-black text-slate-900 tracking-tight italic flex items-center justify-center md:justify-start gap-3">
                 <Settings size={28} className="text-blue-600 animate-[spin_10s_linear_infinite]" /> Control Center
              </h2>
              <div className="flex items-center justify-center md:justify-start gap-3 mt-1">
                 <div className="flex items-center gap-1.5">
                    <div className={`w-1.5 h-1.5 rounded-full ${extensionStatus.available ? 'bg-emerald-500 animate-pulse' : 'bg-red-500'}`} />
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                       {extensionStatus.available ? 'Extension Connected' : 'Extension Offline'}
                    </span>
                 </div>
              </div>
           </div>

           <button
             onClick={() => setShowWizard(true)}
             className="px-8 py-3 bg-slate-900 text-white rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-black hover:shadow-2xl hover:shadow-slate-200 transition-all flex items-center gap-2 italic active:scale-95 shadow-xl shadow-slate-100"
           >
             <Sparkles size={16} className="fill-white" /> Launch Setup Assistant
           </button>
        </div>

        {/* Wizard Progress Section */}
        {!loadingStatus && (
           <div className="bg-slate-900 rounded-[3rem] p-10 text-white shadow-2xl relative overflow-hidden group border border-slate-800">
              <div className="absolute top-0 right-0 p-12 opacity-10 pointer-events-none -rotate-12 group-hover:rotate-0 transition-transform duration-1000">
                 <Cpu size={180} className="text-blue-500" />
              </div>
              
              <div className="relative z-10 flex flex-col md:flex-row items-center gap-10">
                 <div className="relative">
                    <svg className="w-32 h-32 transform -rotate-90">
                       <circle cx="64" cy="64" r="58" stroke="currentColor" strokeWidth="8" fill="transparent" className="text-slate-800" />
                       <circle cx="64" cy="64" r="58" stroke="currentColor" strokeWidth="8" fill="transparent" 
                               className="text-blue-500 transition-all duration-1000 ease-out" 
                               strokeDasharray={364} 
                               strokeDashoffset={364 - (364 * wizardStatus.percentage) / 100} 
                        />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center text-2xl font-black italic">{wizardStatus.percentage}%</div>
                 </div>
                 
                 <div className="flex-1 text-center md:text-left">
                    <h3 className="text-2xl font-black italic tracking-tight mb-2">
                       {wizardStatus.completed ? 'Profile Complete' : 'Setup Required'}
                    </h3>
                    <p className="text-slate-400 text-sm font-medium leading-relaxed max-w-xl">
                       {wizardStatus.completed 
                         ? 'Your profile is fully configured. The Job Agent system is ready for use across all compatible platforms.'
                         : 'Essential setup steps are incomplete. Please run the setup wizard to configure your profile data.'
                       }
                    </p>
                    <div className="flex gap-4 mt-6">
                       {!wizardStatus.completed && (
                       <button 
                          onClick={() => setShowWizard(true)} 
                          className="px-8 py-3 bg-slate-900 text-white rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-black hover:shadow-2xl hover:shadow-slate-200 transition-all flex items-center gap-2 italic active:scale-95 shadow-xl shadow-slate-100"
                       >
                          <Sparkles size={16} className="fill-white" /> Launch Setup Assistant
                       </button>
                       )}
                       <div className="px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-[10px] font-black uppercase tracking-widest text-slate-400 italic flex items-center gap-2">
                          <Database size={14}/> {wizardStatus.completed ? 'Verified' : 'Sync Pending'}
                       </div>
                    </div>
                 </div>
              </div>
           </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          
          {/* Card Component Factory */}
          {[
            {
               id: 'form',
               title: 'Form Automator',
               desc: 'Configure auto-fill logic. Set default values for text fields, radio buttons, and dropdown selections.',
               icon: Sliders,
               color: 'bg-indigo-50 text-indigo-600',
               btn: 'Configure',
               route: '/form-control'
            },
            {
               id: 'cv',
               title: 'Asset Manager',
               desc: 'Deploy and maintain your CV tactical units. Configure title-based filters for automated asset deployment.',
               icon: FileText,
               color: 'bg-blue-50 text-blue-600',
               btn: 'Manage Assets',
               onClick: () => navigate('/dashboard', { state: { tab: 'resumes' } })
            },
            {
               id: 'filter',
               title: 'Defense Filters',
               desc: 'Establish evasion protocols. Define exclusion keywords and description blacklists to avoid low-value targets.',
               icon: Filter,
               color: 'bg-rose-50 text-rose-600',
               btn: 'Defensive Config',
               route: '/filter-settings'
            },
            {
               id: 'external',
               title: 'Target Tracker',
               desc: 'Monitor external mission deployments. Track manual applications and synchronize company-direct data.',
               icon: ExternalLink,
               color: 'bg-emerald-50 text-emerald-600',
               btn: 'View Intel',
               route: '/external-apply'
            }
          ].map(card => (
            <div key={card.id} className="bg-white rounded-[2.5rem] border border-slate-200 p-8 flex flex-col hover:border-blue-300 hover:shadow-2xl hover:shadow-blue-500/5 transition-all duration-300 group relative overflow-hidden">
               <div className="absolute top-0 right-0 p-6 opacity-0 group-hover:opacity-5 transition-all rotate-12 group-hover:rotate-0"><card.icon size={80}/></div>
               <div className={`w-14 h-14 ${card.color} rounded-[1.5rem] flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-inner`}>
                  <card.icon size={24} />
               </div>
               <h3 className="text-xl font-black text-slate-900 mb-2 italic">{card.title}</h3>
               <p className="text-xs text-slate-500 mb-8 flex-1 leading-relaxed font-medium">
                 {card.desc}
               </p>
               <button
                 onClick={() => card.route ? navigate(card.route) : card.onClick()}
                 className="w-full py-3.5 px-4 bg-slate-50 hover:bg-slate-900 hover:text-white text-slate-700 text-[10px] font-black uppercase tracking-widest rounded-2xl border border-slate-200 hover:border-slate-900 transition-all flex items-center justify-center gap-2 italic"
               >
                 {card.btn} <ArrowRight size={14}/>
               </button>
            </div>
          ))}

          {/* Data Management Card (Special Layout) */}
          <div className="bg-white rounded-[2.5rem] border border-slate-200 p-8 flex flex-col hover:border-blue-300 hover:shadow-xl transition-all duration-300 group lg:col-span-2">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-8 pb-8 border-b border-slate-100">
               <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-slate-100 text-slate-500 rounded-[1.5rem] flex items-center justify-center shadow-inner">
                     <Database size={24} />
                  </div>
                  <div>
                     <h3 className="text-xl font-black text-slate-900 italic">Data Management</h3>
                     <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Data Backup & Restore</p>
                  </div>
               </div>
               <div className="flex items-center gap-1.5 px-4 py-2 bg-emerald-50 rounded-xl text-emerald-600 border border-emerald-100 shadow-sm shadow-emerald-50">
                  <ShieldCheck size={14} />
                  <span className="text-[9px] font-black uppercase tracking-widest">Secure Export Enabled</span>
               </div>
            </div>

            <p className="text-xs text-slate-500 mb-10 leading-relaxed font-medium italic">
               Synchronize your tactical configuration across multiple deployment nodes. Exporting will generate a secure JSON payload containing all profile attributes and neural form logic.
            </p>

            <div className="flex flex-wrap gap-4 mt-auto">
              <button
                onClick={handleExport}
                disabled={exporting}
                className="flex-1 min-w-[150px] py-4 px-6 bg-slate-900 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-black transition-all flex items-center justify-center gap-3 italic shadow-xl shadow-slate-200 disabled:opacity-50"
              >
                {exporting ? <RefreshCw size={16} className="animate-spin" /> : <Download size={16} />}
                {exporting ? 'Extracting...' : 'Export Payload'}
              </button>
              <button
                onClick={handleImport}
                disabled={importing}
                className="flex-1 min-w-[150px] py-4 px-6 bg-white border-2 border-slate-200 text-slate-700 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-50 hover:border-slate-300 transition-all flex items-center justify-center gap-3 italic disabled:opacity-50"
              >
                {importing ? <RefreshCw size={16} className="animate-spin" /> : <Upload size={16} />}
                {importing ? 'Syncing...' : 'Import Payload'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default SettingsPage;
